// PR-26: mobile companion shell.
// PR-31: mobile aligns with desktop — full chat (multi-thread + history) +
// side drawer for project/thread navigation. Bottom tabs: Chat / Cards / Gaps.
//
// Architecture changes vs PR-26:
//   - Capture tab retired; sending a message in the Chat tab IS the capture.
//     The agent decides when to write_card (matches desktop semantics).
//   - User-visible threads with history (was: a single hidden "Mobile Capture"
//     thread). Threads list lives in MobileSideDrawer; tap to switch.
//   - First-message-on-an-empty-shell auto-creates a thread (mirrors desktop).
//   - Active thread persisted via api.setLastThread so a reload lands back
//     on the same conversation.
//
// Things deferred to later PRs (still TODO):
//   - PR-32: full DiscussionPane parity (tool-call chips, thinking expand,
//     long-press action sheet, wikilink cross-tab jump, markdown rendering).
//   - PR-33: Cards tab → frame view (PR-31 leaves the flat list as-is).
//   - PR-34: Gaps tab → 3-segment view (gaps / stale ripple / frame gaps),
//     real Discuss-button cross-tab jump.
//   - PR-35: Distill / Documents entry, Settings.
//
// Offline queue (PR-28) preserved: queued captures still flush through the
// legacy "Mobile Capture" thread to avoid dumping replayed entries into
// whatever the user's currently active thread is.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, createThreadWithPreference, isMyTurn, setPreferredTier } from '../../api'
import type {
  Card,
  CardPatch,
  CardProposal,
  DistillMode,
  GapProposal,
  MeResponse,
  ModelChoices,
  Project,
  Thread,
  ThreadUsage,
  TurnEvent,
} from '../../api'
import type { FrameGapInfo } from '../CardsPanel'
import './mobile.css'

import { MobileTopBar } from './MobileTopBar'
import { MobileBottomNav } from './MobileBottomNav'
import { MobileChat } from './MobileChat'
import { MobileCards } from './MobileCards'
import { MobileGaps } from './MobileGaps'
import { MobileCardPeek } from './MobileCardPeek'
import { MobileSideDrawer } from './MobileSideDrawer'
import { MobileDistillModal } from './MobileDistillModal'
import { MobileDocumentsModal } from './MobileDocumentsModal'
import type { TopBarAction } from './MobileTopBar'
import type { MobileChatMessage } from './chat_types'
import { buildDiscussingGapWire, stripDiscussingGapWire } from './discuss_wire'
import { listenToAppResume } from '../../native/app_state'
import { useNetworkStatus } from '../../hooks/useNetworkStatus'
import * as cardsCache from '../../storage/cards_cache'
import * as projectsCache from '../../storage/projects_cache'
import * as captureQueue from '../../storage/capture_queue'
import { isNetworkError } from '../../storage/network_error'
import { reportClientError } from '../../error_capture'
import { isWeChatWebView } from '../../utils/wechat'
import { isCapacitor } from '../../native/platform'

export type MobileTab = 'chat' | 'cards' | 'gaps'

// Stable empty array for the active-messages memo, so a thread with no
// content keeps the same reference across renders and downstream memos
// don't churn just because we re-evaluated.
const EMPTY_MESSAGES: MobileChatMessage[] = []

/**
 * Flatten persisted session messages into MobileChatMessage[]. Mirrors
 * the per-message logic inside the thread-load effect; called from the
 * project SSE `thread_turn_ended` handler so observer devices land on
 * the canonical post-turn message list without waiting for the user to
 * close and reopen the thread.
 */
function flattenSessionToMobile(
  session: Awaited<ReturnType<typeof api.listMessages>>,
): MobileChatMessage[] {
  const flat: MobileChatMessage[] = []
  for (const m of session) {
    if (m.role !== 'user' && m.role !== 'assistant') continue
    for (const b of m.blocks) {
      if (b.type === 'text') {
        const text =
          m.role === 'user' ? stripDiscussingGapWire(b.text) : b.text
        if (!text || !text.trim()) continue
        flat.push({ kind: m.role, text, ts: Date.now() })
      } else if (b.type === 'tool_use' && m.role === 'assistant') {
        flat.push({
          kind: 'tool_call',
          tool: b.name,
          input: b.input,
          ts: Date.now(),
          toolUseId: b.id,
        })
      } else if (b.type === 'thinking' && m.role === 'assistant' && b.text) {
        flat.push({
          kind: 'thinking',
          text: b.text,
          done: true,
          ts: Date.now(),
        })
      }
    }
  }
  return flat
}

interface MobileShellProps {
  me: MeResponse
  /** Logout handler — same wiring as the desktop sidebar. App.tsx clears
   *  authState afterwards which unmounts the shell. */
  onLogout: () => void | Promise<void>
}

export function MobileShell({ me, onLogout }: MobileShellProps) {
  const [projects, setProjects] = useState<Project[]>([])
  const [project, setProject] = useState<Project | null>(null)
  const [cards, setCards] = useState<Card[]>([])

  // Threads + per-thread chat state (mirrors desktop App.tsx pattern; lets
  // the user start a turn in one thread and switch tabs / drawers without
  // losing the in-flight stream's slot).
  const [threads, setThreads] = useState<Thread[]>([])
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null)
  // PR-58 mobile: model picker state. Fetched once per session — the
  // env-driven tier list doesn't change without a server restart, so
  // caching for the lifetime of the SPA tab is fine. Null while loading;
  // the chip stays hidden until choices land.
  const [modelChoices, setModelChoices] = useState<ModelChoices | null>(null)
  const [messagesByThread, setMessagesByThread] = useState<
    Record<string, MobileChatMessage[]>
  >({})
  // Per-thread context-window snapshot. Hydrated from getThreadUsage on
  // first thread open; refreshed via the SSE `turn_usage` event after
  // each run_turn. Powers the MobileTokenUsageChip in MobileChat header.
  const [usageByThread, setUsageByThread] = useState<Record<string, ThreadUsage>>({})
  // Declared early so the streaming helpers below can reference setError
  // without hitting a temporal dead zone (React Compiler flags forward
  // references). Other transient banner state stays grouped near the
  // bottom — only `setError` is needed up here.
  const [error, setError] = useState<string | null>(null)
  // Streaming buffer: events from the in-flight turn append HERE, not into
  // `messagesByThread`. The render below merges the two. Keeping history
  // immutable for the duration of the turn lets the per-row React.memo on
  // `BubbleRow` skip every historical bubble per token, so a 200-bubble
  // session no longer pays O(N) reconcile per delta.
  //
  // All updates flow through the functional-updater form so the React
  // Compiler doesn't think downstream callbacks read a ref during render.
  // Token deltas at 50/s are still cheap because each updater touches
  // only the active thread's slot.
  const [streamingByThread, setStreamingByThread] = useState<
    Record<string, MobileChatMessage[]>
  >({})
  // Mirror of `streamingByThread` for synchronous reads in `commitStreaming`.
  // The earlier "closure variable shared between two setState updaters"
  // pattern broke under React 19 StrictMode: the first updater is replayed
  // with the just-computed new state, so the closure variable ended up
  // overwritten with the "empty" branch's value and the assistant bubble
  // was lost on commit. Reading via ref + dispatching with already-computed
  // values keeps the updaters pure and the commit deterministic.
  const streamingByThreadRef = useRef<Record<string, MobileChatMessage[]>>({})
  const updateStreamArr = useCallback(
    (
      tid: string,
      updater: (prev: MobileChatMessage[]) => MobileChatMessage[],
    ) => {
      const cur = streamingByThreadRef.current
      const prev = cur[tid] ?? []
      const next = updater(prev)
      if (next === prev) return
      const updated = { ...cur, [tid]: next }
      streamingByThreadRef.current = updated
      setStreamingByThread(updated)
    },
    [],
  )
  const dropStreaming = useCallback((tid: string) => {
    const cur = streamingByThreadRef.current
    if (!(tid in cur)) return
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [tid]: _gone, ...rest } = cur
    streamingByThreadRef.current = rest
    setStreamingByThread(rest)
  }, [])
  const dropAllStreaming = useCallback(() => {
    if (Object.keys(streamingByThreadRef.current).length === 0) return
    streamingByThreadRef.current = {}
    setStreamingByThread({})
  }, [])

  // Shared TurnEvent → streaming-buffer reducer. Both the chat send path
  // and the gap-discuss opener path stream through this so their bubble
  // rendering is identical and stays in sync if the protocol grows new
  // event types.
  //
  // `error` is a side effect (toast); pure-structural events flow into
  // `updateStreamArr`. Returns nothing — caller doesn't branch on result.
  const handleStreamEvent = useCallback(
    (
      tid: string,
      evt: TurnEvent,
      toolNamesById?: Map<string, string>,
    ) => {
      switch (evt.type) {
        case 'assistant_start':
          // Intentionally no-op: pushing an empty assistant bubble here
          // strands a blank rectangle before `thinking_delta` (which opens
          // its own bubble) and the eventual `text_delta` (which opens a
          // *second* assistant bubble after the thinking one). Let
          // `text_delta` create the bubble lazily — matches desktop and
          // lets MobileChat's "✦ thinking…" inline indicator show in the
          // gap before the first reasoning chunk arrives.
          break
        case 'text_delta':
          updateStreamArr(tid, (prev) => {
            const last = prev[prev.length - 1]
            if (last && last.kind === 'assistant' && last.streaming) {
              const u = prev.slice()
              u[u.length - 1] = { ...last, text: last.text + evt.text }
              return u
            }
            return [
              ...prev,
              {
                kind: 'assistant',
                text: evt.text,
                ts: Date.now(),
                streaming: true,
              },
            ]
          })
          break
        case 'assistant_end':
          updateStreamArr(tid, (prev) => {
            const last = prev[prev.length - 1]
            if (!last || last.kind !== 'assistant' || !last.streaming) {
              return prev
            }
            const u = prev.slice()
            u[u.length - 1] = {
              ...last,
              streaming: false,
              truncated:
                evt.truncated === true ? true : last.truncated,
            }
            return u
          })
          break
        case 'thinking_delta':
          updateStreamArr(tid, (prev) => {
            const last = prev[prev.length - 1]
            if (last && last.kind === 'thinking' && !last.done) {
              const u = prev.slice()
              u[u.length - 1] = { ...last, text: last.text + evt.text }
              return u
            }
            return [
              ...prev,
              {
                kind: 'thinking',
                text: evt.text,
                done: false,
                ts: Date.now(),
              },
            ]
          })
          break
        case 'thinking_end':
          updateStreamArr(tid, (prev) => {
            const last = prev[prev.length - 1]
            if (!last || last.kind !== 'thinking' || last.done) return prev
            const u = prev.slice()
            u[u.length - 1] = { ...last, done: true }
            return u
          })
          break
        case 'tool_use_start':
          if (toolNamesById) toolNamesById.set(evt.id, evt.name)
          updateStreamArr(tid, (prev) => [
            ...prev,
            {
              kind: 'tool_call',
              tool: evt.name,
              input: '',
              ts: Date.now(),
              toolUseId: evt.id,
            },
          ])
          break
        case 'tool_use_input_delta':
          updateStreamArr(tid, (prev) => {
            for (let i = prev.length - 1; i >= 0; i--) {
              const m = prev[i]
              if (m.kind === 'tool_call' && m.toolUseId === evt.id) {
                const u = prev.slice()
                u[i] = { ...m, input: m.input + evt.partial }
                return u
              }
            }
            return prev
          })
          break
        case 'tool_use_end':
          updateStreamArr(tid, (prev) => {
            for (let i = prev.length - 1; i >= 0; i--) {
              const m = prev[i]
              if (m.kind === 'tool_call' && m.toolUseId === evt.id) {
                const u = prev.slice()
                u[i] = { ...m, input: evt.input }
                return u
              }
            }
            return prev
          })
          break
        case 'turn_usage': {
          // PR-55 / PR-58: server emits this once per turn just before
          // turn_done. Cache against the ORIGIN thread (`tid` in this
          // closure), not whatever the user is viewing — a turn that
          // started while the user was in thread A can complete after
          // they switch to thread B, and we want A's slot updated.
          setUsageByThread((prev) => ({
            ...prev,
            [tid]: {
              session_total_input: evt.session_total_input,
              session_total_output: evt.session_total_output,
              session_total_cache_creation: evt.session_total_cache_creation,
              session_total_cache_read: evt.session_total_cache_read,
              pre_compact_messages: evt.pre_compact_messages,
              post_compact_messages: evt.post_compact_messages,
              system_tokens: evt.system_tokens,
              tools_tokens: evt.tools_tokens,
              compact_triggered: evt.compact_triggered,
              compacted_message_count: evt.compacted_message_count,
              context_window: evt.context_window,
              compact_threshold: evt.compact_threshold,
              estimated_next_input: evt.estimated_next_input,
            },
          }))
          break
        }
        case 'error':
          setError(`agent: ${evt.message}`)
          break
        // tool_result / turn_done are not surfaced in the UI.
      }
    },
    [updateStreamArr],
  )

  // Promote the streaming buffer into permanent message history at turn
  // end (success OR failure). Any bubble still flagged streaming/!done
  // gets finalized so a network drop mid-turn leaves the partial reply
  // visible and frozen rather than blinking forever.
  //
  // Computes `finalized` synchronously from `streamingByThreadRef` so the
  // value is captured before either setState runs. Earlier we used a
  // closure variable assigned inside the setStreamingByThread updater and
  // read in the setMessagesByThread updater — that broke under React 19
  // StrictMode, which replays the first updater with the just-computed new
  // state and the closure ended up overwritten with the empty-branch's
  // value, so the assistant bubble silently disappeared after each turn.
  const commitStreaming = useCallback((tid: string) => {
    const segment = streamingByThreadRef.current[tid] ?? []
    const finalized: MobileChatMessage[] = segment.map((m): MobileChatMessage => {
      if (m.kind === 'assistant' && m.streaming) return { ...m, streaming: false }
      if (m.kind === 'thinking' && !m.done) return { ...m, done: true }
      return m
    })
    // Drop the streaming entry from both ref and state.
    if (tid in streamingByThreadRef.current) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [tid]: _gone, ...rest } = streamingByThreadRef.current
      streamingByThreadRef.current = rest
      setStreamingByThread(rest)
    }
    // Append finalized bubbles to the thread's permanent history.
    if (finalized.length > 0) {
      setMessagesByThread((mPrev) => ({
        ...mPrev,
        [tid]: [...(mPrev[tid] ?? []), ...finalized],
      }))
    }
  }, [])
  const [busyByThread, setBusyByThread] = useState<Record<string, boolean>>({})
  // Mirror of desktop App.tsx — track which project each ever-busy thread
  // belongs to, so the side drawer can dot off-screen project rows when
  // their inference is still running. Populated at busy-on time (we know
  // project.id then); persists past busy-off so a re-busy doesn't need a
  // re-mapping round-trip.
  const [threadProjectMap, setThreadProjectMap] = useState<Record<string, string>>({})
  // PR-32: per-thread pinned gap (cardId of the gap the user is "discussing").
  // Mirrors desktop pinnedGapByThread. Set in PR-34 by the Gaps tab Discuss
  // button; cleared by Resolve/Approve/Reject in MobileChat's banner.
  const [pinnedGapByThread, setPinnedGapByThread] = useState<
    Record<string, string>
  >({})

  // Mirrored to sessionStorage so a refresh stays on the same bottom
  // tab. Drawer/peek/modals are deliberately not persisted — they're
  // transient overlays.
  const [tab, setTab] = useState<MobileTab>(() => {
    try {
      const v = sessionStorage.getItem('draftlize:mobile.tab')
      return v === 'cards' || v === 'gaps' ? v : 'chat'
    } catch {
      return 'chat'
    }
  })
  useEffect(() => {
    try {
      sessionStorage.setItem('draftlize:mobile.tab', tab)
    } catch {}
  }, [tab])
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [peekedCardId, setPeekedCardId] = useState<string | null>(null)

  // PR-33: cards revision counter — bumped whenever the cards list mutates
  // so child views (MobileCards' frame view) know to refetch their derived
  // state. Mirrors desktop App.tsx pattern. We derive from a useEffect on
  // `cards` so callers don't have to remember to bump it manually after
  // every setCards.
  const [cardsRevision, setCardsRevision] = useState(0)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCardsRevision((r) => r + 1)
  }, [cards])
  // PR-33: frame MECE gaps surfaced from the active frame in MobileCards.
  // PR-34 renders them in the Gaps tab's "FRAME GAPS" section.
  const [frameGaps, setFrameGaps] = useState<FrameGapInfo[]>([])

  // PR-35: distill flow state. Mirrors desktop App.tsx; one set of state
  // per shell because at most one distill run is ever in flight.
  const [distillOpen, setDistillOpen] = useState(false)
  const [distilling, setDistilling] = useState(false)
  const [distillMode, setDistillMode] = useState<DistillMode>('rescue')
  const [distillItems, setDistillItems] = useState(0)
  const [distillCards, setDistillCards] = useState<CardProposal[] | null>(null)
  const [distillGaps, setDistillGaps] = useState<GapProposal[] | null>(null)
  const [distillError, setDistillError] = useState<string | null>(null)

  // PR-35: read-only Documents modal.
  const [documentsOpen, setDocumentsOpen] = useState(false)

  const [toast, setToast] = useState<string | null>(null)
  // Optional action button next to the toast (e.g. "Undo" after a bulk
  // approve). Lifecycle is independent: setting `toastAction` schedules
  // a longer auto-clear (5s vs 2.2s) so the user has time to react. Tap
  // anywhere on the toast row that ISN'T the action button still
  // dismisses both.
  const [toastAction, setToastAction] = useState<{
    label: string
    onClick: () => void | Promise<void>
  } | null>(null)
  const showUndoToast = useCallback(
    (text: string, action: { label: string; onClick: () => void | Promise<void> }) => {
      setToast(text)
      setToastAction(action)
    },
    [],
  )
  const [bootStage, setBootStage] = useState<'loading' | 'ready' | 'empty'>('loading')

  // PR-30: WeChat in-app browser detection. Show a one-line tip on first
  // mount, dismissible. Skip when running inside Capacitor (the native
  // shell is a real browser context already).
  const [showWechatBanner, setShowWechatBanner] = useState<boolean>(
    () => !isCapacitor() && isWeChatWebView(),
  )

  // PR-28: offline state + queued-capture count for the top-bar chip.
  const { online } = useNetworkStatus()
  const [queueCount, setQueueCount] = useState(0)
  const refreshQueueCount = useCallback(async () => {
    const all = await captureQueue.listAll()
    setQueueCount(all.length)
  }, [])

  // Auto-clear transient banners. Same 2s/4s convention as the desktop app.
  // Action toasts (Undo) get 5s — long enough to read + react.
  useEffect(() => {
    if (!toast) return
    const ms = toastAction ? 5000 : 2200
    const t = setTimeout(() => {
      setToast(null)
      setToastAction(null)
    }, ms)
    return () => clearTimeout(t)
  }, [toast, toastAction])
  useEffect(() => {
    if (!error) return
    const t = setTimeout(() => setError(null), 4000)
    return () => clearTimeout(t)
  }, [error])

  // ── Cards loading ────────────────────────────────────────────────
  // Network fetch + write-through to IndexedDB. Errors surface as a
  // toast UNLESS we're offline + have a cached list — then we just keep
  // the cache and stay quiet.
  const refreshCards = useCallback(async (projectId: string) => {
    try {
      const cs = await api.listCards(projectId)
      setCards(cs)
      cardsCache.write(projectId, cs).catch(() => {})
    } catch (e) {
      if (!isNetworkError(e)) {
        setError(`load cards failed: ${(e as Error).message}`)
      }
    }
  }, [])

  // ── Threads loading ──────────────────────────────────────────────
  // Refresh the thread list for a project. On success picks an active
  // thread if none is set: prefers the project's `last_thread` (from
  // me.project_views), falls back to the first non-archived thread, then
  // null (the user can hit + NEW or just type to auto-create).
  const refreshThreads = useCallback(
    async (projectId: string, opts?: { selectInitial?: boolean }) => {
      try {
        const ths = await api.listThreads(projectId)
        setThreads(ths)
        if (opts?.selectInitial) {
          const lastId = me.project_views?.[projectId] ?? null
          const candidate =
            (lastId && ths.find((t) => t.id === lastId && !t.archived)) ||
            ths.find((t) => !t.archived) ||
            null
          setActiveThreadId(candidate ? candidate.id : null)
        }
        return ths
      } catch (e) {
        if (!isNetworkError(e)) {
          setError(`load threads failed: ${(e as Error).message}`)
        }
        return null
      }
    },
    [me.project_views],
  )

  // ── Project switching ────────────────────────────────────────────
  const selectProject = useCallback(
    async (next: Project) => {
      setProject(next)
      setPeekedCardId(null)
      setActiveThreadId(null)
      setMessagesByThread({})
      dropAllStreaming()
      setBusyByThread({})
      const cached = await cardsCache.read(next.id)
      setCards(cached ?? [])
      api.setLastProject(next.id).catch(() => {})
      await Promise.all([
        refreshCards(next.id),
        refreshThreads(next.id, { selectInitial: true }),
      ])
    },
    [refreshCards, refreshThreads, dropAllStreaming],
  )

  // ── Boot ─────────────────────────────────────────────────────────
  // PR-28: try the cache first for both projects + cards so the user sees
  // their last-active project's data INSTANTLY. Network fetch follows.
  // PR-31: ALSO load threads on boot so the drawer can render immediately.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const cachedProjects = await projectsCache.read()
      if (cancelled) return
      let initialProjectId: string | null = null
      if (cachedProjects && cachedProjects.length > 0) {
        setProjects(cachedProjects)
        const initial =
          cachedProjects.find((p) => p.id === me.last_project_id) ?? cachedProjects[0]
        setProject(initial)
        initialProjectId = initial.id
        const cachedCards = await cardsCache.read(initial.id)
        if (cancelled) return
        if (cachedCards) setCards(cachedCards)
        setBootStage('ready')
      }
      // PR-58 mobile: warm the model picker cache. Failure is non-fatal
       // — chip just stays hidden until next page load. Fired in
       // parallel with the projects fetch.
       api.getModelChoices().then(setModelChoices).catch(() => {})
      try {
        const ps = await api.listProjects()
        if (cancelled) return
        setProjects(ps)
        projectsCache.write(ps).catch(() => {})
        if (ps.length === 0) {
          setBootStage('empty')
          return
        }
        const initial = ps.find((p) => p.id === me.last_project_id) ?? ps[0]
        if (!initialProjectId) {
          setProject(initial)
          await Promise.all([
            refreshCards(initial.id),
            refreshThreads(initial.id, { selectInitial: true }),
          ])
        } else if (initialProjectId === initial.id) {
          await Promise.all([
            refreshCards(initial.id),
            refreshThreads(initial.id, { selectInitial: true }),
          ])
        }
        if (!cancelled) setBootStage('ready')
      } catch (e) {
        if (cancelled) return
        if (cachedProjects && cachedProjects.length > 0) {
          // Already showing cache; nothing more to do.
        } else {
          setError(`load failed: ${(e as Error).message}`)
          setBootStage('ready')
        }
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── SSE: react to backend changes for the active project ────────
  useEffect(() => {
    if (!project) return
    return api.events(project.id, async (evt) => {
      if (evt.type === 'documents_stale') return
      // Turn lifecycle from any device on this project. `thread_turn_started`
      // arrives both for our own POST (we already setBusy) and for another
      // device's POST (we need to setBusy here so the chat header shows
      // "responding"). Idempotent: setting busy=true twice is fine.
      if (evt.type === 'thread_turn_started') {
        setBusyByThread((prev) =>
          prev[evt.thread_id] ? prev : { ...prev, [evt.thread_id]: true },
        )
        return
      }
      if (evt.type === 'thread_turn_ended') {
        const tid = evt.thread_id
        // Skip refetch on the originator — local stream already has the
        // richest view (including thinking bubbles the server doesn't
        // persist). Observer devices still refetch to land on the same
        // canonical post-turn messages.
        if (!isMyTurn(evt.turn_id)) {
          try {
            const session = await api.listMessages(tid)
            const flat = flattenSessionToMobile(session)
            setMessagesByThread((prev) => ({ ...prev, [tid]: flat }))
          } catch {
            /* non-fatal — next thread open will refetch fresh */
          }
          try {
            const usage = await api.getThreadUsage(tid)
            setUsageByThread((prev) => ({ ...prev, [tid]: usage }))
          } catch {
            /* usage refresh is best-effort */
          }
        }
        setBusyByThread((prev) => ({ ...prev, [tid]: false }))
        return
      }
      try {
        const fresh = await api.listCards(project.id)
        setCards(fresh)
      } catch {
        /* SSE refresh failures are non-fatal — next event reconciles */
      }
    })
  }, [project?.id])

  // Check on every thread open whether a turn is currently running
  // server-side. If yes, flip busy so the composer queues new sends
  // instead of trying to start a parallel turn (which would 409).
  // Project SSE `thread_turn_ended` will clear busy and refetch when
  // the running turn completes.
  useEffect(() => {
    if (!activeThreadId) return
    const tid = activeThreadId
    let cancelled = false
    void api
      .getActiveTurn(tid)
      .then((active) => {
        if (cancelled || !active) return
        setBusyByThread((prev) =>
          prev[tid] ? prev : { ...prev, [tid]: true },
        )
      })
      .catch(() => {
        /* non-fatal */
      })
    return () => {
      cancelled = true
    }
  }, [activeThreadId])

  // ── Lazy-load messages on thread switch ─────────────────────────
  // When the user picks a thread we don't yet have cached messages for,
  // fetch them. Skip if the slot already exists (even an empty one — that
  // means a thread was just created and has no messages yet).
  //
  // PR-32: hydrate tool_use blocks as tool_call entries so historical
  // messages render the same chips as live ones. Thinking blocks aren't
  // persisted in session.messages on the server (the runtime drops them
  // post-stream); historical reload won't show reasoning, matching desktop.
  useEffect(() => {
    if (!activeThreadId) return
    if (activeThreadId in messagesByThread) return
    const tid = activeThreadId
    let cancelled = false
    ;(async () => {
      try {
        const session = await api.listMessages(tid)
        if (cancelled) return
        const flat: MobileChatMessage[] = []
        for (const m of session) {
          if (m.role !== 'user' && m.role !== 'assistant') continue
          for (const b of m.blocks) {
            if (b.type === 'text') {
              // User messages sent during a pinned-gap discussion carry a
              // `[discussing-gap c-X] ... USER: <text>` envelope so the
              // agent sees full gap context. Strip it on reload so the
              // bubble shows just `<text>`. Synthetic openers return null
              // and are skipped (they never render as a user bubble).
              const text =
                m.role === 'user' ? stripDiscussingGapWire(b.text) : b.text
              if (!text || !text.trim()) continue
              flat.push({ kind: m.role, text, ts: Date.now() })
            } else if (b.type === 'tool_use' && m.role === 'assistant') {
              flat.push({
                kind: 'tool_call',
                tool: b.name,
                input: b.input,
                ts: Date.now(),
                toolUseId: b.id,
              })
            } else if (b.type === 'thinking' && m.role === 'assistant' && b.text) {
              // Persisted reasoning — `done: true` so the bubble renders
              // already-collapsed (live streams flip it on thinking_end).
              flat.push({
                kind: 'thinking',
                text: b.text,
                done: true,
                ts: Date.now(),
              })
            }
            // tool_result blocks are skipped — desktop also doesn't render them.
          }
        }
        setMessagesByThread((prev) => ({ ...prev, [tid]: flat }))
      } catch (e) {
        if (!isNetworkError(e)) {
          setError(`load messages failed: ${(e as Error).message}`)
        }
        setMessagesByThread((prev) =>
          prev[tid] ? prev : { ...prev, [tid]: [] },
        )
      }
    })()
    return () => {
      cancelled = true
    }
  }, [activeThreadId, messagesByThread])

  // Hydrate the context-usage snapshot. Kept as a SEPARATE effect from
  // the messages loader because depending on `messagesByThread` here
  // would cancel the in-flight getThreadUsage every time setMessagesByThread
  // committed (cleanup → cancelled=true → result drop). Keys only on
  // activeThreadId; SSE turn_usage refreshes it from then on.
  useEffect(() => {
    if (!activeThreadId) return
    if (activeThreadId in usageByThread) return
    const tid = activeThreadId
    let cancelled = false
    api.getThreadUsage(tid)
      .then((usage) => {
        if (!cancelled) {
          setUsageByThread((prev) => ({ ...prev, [tid]: usage }))
        }
      })
      .catch(() => {
        /* leave unset; SSE turn_usage will populate after the next turn */
      })
    return () => {
      cancelled = true
    }
  }, [activeThreadId, usageByThread])

  // ── Send a chat message ────────────────────────────────────────
  // Auto-creates a thread on first send if none is active (mirrors desktop).
  // PR-32: handles the full TurnEvent surface — assistant_start/end,
  // text_delta, thinking_delta/end, tool_use_start/input_delta/end. Pinned
  // gaps expand the wire payload via buildDiscussingGapWire (mirrors desktop
  // App.tsx onSend).
  const sendingRef = useRef(false)
  const onSendChatMessage = useCallback(
    async (text: string) => {
      if (!project || sendingRef.current) return
      const trimmed = text.trim()
      if (!trimmed) return
      sendingRef.current = true
      try {
        // Offline → queue with the legacy capture wrapper so the existing
        // queue replay path keeps working without changes.
        if (!online) {
          await captureQueue.enqueue(project.id, trimmed)
          setToast('Queued — will sync when online')
          await refreshQueueCount()
          return
        }

        // Resolve the destination thread; create a new one if there's no
        // active thread.
        let threadId = activeThreadId
        if (!threadId) {
          try {
            const t = await createThreadWithPreference(project.id, null)
            setThreads((prev) => [t, ...prev])
            threadId = t.id
            // Seed an empty slot so the load effect doesn't try to fetch.
            setMessagesByThread((prev) => ({ ...prev, [t.id]: [] }))
            setActiveThreadId(t.id)
            api.setLastThread(project.id, t.id).catch(() => {})
          } catch (e) {
            setError(`create thread failed: ${(e as Error).message}`)
            return
          }
        }
        const tid = threadId

        // PR-32: if the active thread is pinned to a gap, expand the wire
        // payload so the agent sees the full gap context (matches desktop).
        // The user bubble still shows just `trimmed` — the bookkeeping
        // header is invisible in the UI.
        const pinnedCardId = pinnedGapByThread[tid] ?? null
        const pinnedCard =
          pinnedCardId
            ? cards.find((c) => c.card_id === pinnedCardId && c.kind === 'gap') ?? null
            : null
        const wireText = pinnedCard
          ? buildDiscussingGapWire(pinnedCard, cards, trimmed)
          : trimmed

        // Optimistic user bubble lands directly in history (it's already
        // "complete" — the user typed it). Streaming bubbles flow into
        // streamingByThread instead.
        setMessagesByThread((prev) => ({
          ...prev,
          [tid]: [
            ...(prev[tid] ?? []),
            { kind: 'user', text: trimmed, ts: Date.now() },
          ],
        }))
        setBusyByThread((prev) => ({ ...prev, [tid]: true }))
        setThreadProjectMap((prev) =>
          prev[tid] === project.id ? prev : { ...prev, [tid]: project.id },
        )

        // Auto-name a still-untitled thread from the first user turn.
        // Cosmetic; failure is silent.
        const currentTitle = threads.find((t) => t.id === tid)?.title ?? null
        if ((currentTitle ?? '').trim() === '') {
          const derived = deriveThreadTitle(trimmed)
          if (derived) {
            setThreads((arr) =>
              arr.map((t) => (t.id === tid ? { ...t, title: derived } : t)),
            )
            api.renameThread(tid, derived).then(
              (updated) =>
                setThreads((arr) =>
                  arr.map((t) => (t.id === tid ? updated : t)),
                ),
              () => {/* cosmetic */},
            )
          }
        }

        // Track active tool-use ids while streaming so input_delta chunks
        // can merge into the right chip.
        const toolNamesById = new Map<string, string>()

        try {
          await api.streamMessage(tid, wireText, (evt: TurnEvent) => {
            handleStreamEvent(tid, evt, toolNamesById)
          })
        } catch (e) {
          if (isNetworkError(e)) {
            // Network died mid-stream. Roll back to a queued capture so it
            // still gets through later.
            await captureQueue.enqueue(project.id, trimmed)
            setToast('Queued — will retry')
            await refreshQueueCount()
          } else {
            // Non-network failure (auth / 500 / validation) — surface to
            // the user AND report to the error pipeline so admin tooling
            // can correlate FE error → BE log via x-request-id.
            reportClientError(e, {
              action: 'mobile.chat.send',
              project_id: project.id,
              thread_id: tid,
              text_len: trimmed.length,
              navigator_online: navigator.onLine,
            })
            setError(`send failed: ${(e as Error).message}`)
          }
        } finally {
          // Promote whatever streamed (full or partial) into history so
          // the next turn sees it as immutable past content.
          commitStreaming(tid)
          setBusyByThread((prev) => {
            if (!(tid in prev)) return prev
            const { [tid]: _gone, ...rest } = prev
            return rest
          })
          // Optimistic last_message_at bump so the unread dot surfaces
          // for off-screen viewers (mirror of desktop App.tsx).
          // Cross-project bump is intentionally omitted on mobile —
          // mobile is mostly single-project; the projects list refetches
          // on every project switch and will pick up the truth there.
          const finishedAt = new Date().toISOString()
          const userIsViewing = activeThreadId === tid
          setThreads((arr) =>
            arr.map((t) =>
              t.id === tid
                ? {
                    ...t,
                    last_message_at: finishedAt,
                    updated_at: finishedAt,
                    last_read_at: userIsViewing ? finishedAt : t.last_read_at,
                  }
                : t,
            ),
          )
          if (userIsViewing) {
            api.markThreadRead(tid).catch(() => {})
          }
        }
      } finally {
        sendingRef.current = false
      }
    },
    [project, online, activeThreadId, threads, refreshQueueCount, pinnedGapByThread, cards, handleStreamEvent, commitStreaming],
  )

  // ── Thread CRUD handlers (drawer-driven) ─────────────────────────
  const onSelectThread = useCallback(
    (id: string) => {
      setActiveThreadId(id)
      setTab('chat') // switching threads always lands the user in chat
      if (project) {
        api.setLastThread(project.id, id).catch(() => {})
      }
      // Optimistic mark-read (mirrors desktop App.tsx). Same best-effort
      // semantics — silent failure since the server cursor will sync on
      // the next list_threads.
      const now = new Date().toISOString()
      setThreads((arr) =>
        arr.map((t) => (t.id === id ? { ...t, last_read_at: now } : t)),
      )
      api.markThreadRead(id).catch(() => {})
    },
    [project],
  )

  const onNewThread = useCallback(async () => {
    if (!project) return
    try {
      const t = await createThreadWithPreference(project.id, null)
      setThreads((prev) => [t, ...prev])
      setMessagesByThread((prev) => ({ ...prev, [t.id]: [] }))
      setActiveThreadId(t.id)
      setTab('chat')
      api.setLastThread(project.id, t.id).catch(() => {})
    } catch (e) {
      setError(`new thread failed: ${(e as Error).message}`)
    }
  }, [project])

  const onRenameThread = useCallback(
    async (id: string, title: string) => {
      const prev = threads
      setThreads((arr) =>
        arr.map((t) => (t.id === id ? { ...t, title } : t)),
      )
      try {
        const updated = await api.renameThread(id, title)
        setThreads((arr) => arr.map((t) => (t.id === id ? updated : t)))
      } catch (e) {
        setThreads(prev)
        setError(`rename failed: ${(e as Error).message}`)
      }
    },
    [threads],
  )

  const onArchiveThread = useCallback(
    async (id: string) => {
      // PR-31: no extra confirm dialog — the drawer's long-press →
      // action sheet → "Archive" tap is already a 2-step gesture.
      // Mid-turn archive is blocked (matches desktop's safety check).
      if (busyByThread[id]) {
        setError('cannot archive a thread mid-turn — wait for it to finish')
        return
      }
      try {
        await api.archiveThread(id)
        setThreads((prev) => prev.filter((t) => t.id !== id))
        setMessagesByThread((prev) => {
          if (!(id in prev)) return prev
          const { [id]: _gone, ...rest } = prev
          return rest
        })
        dropStreaming(id)
        if (activeThreadId === id) {
          setActiveThreadId(null)
          if (project) {
            api.setLastThread(project.id, null).catch(() => {})
          }
        }
        setToast('Thread archived')
      } catch (e) {
        setError(`archive failed: ${(e as Error).message}`)
      }
    },
    [activeThreadId, busyByThread, project, dropStreaming],
  )

  // ── Offline capture queue (PR-28) ────────────────────────────────
  // Queue replay still flows through a dedicated "Mobile Capture" thread
  // so replayed entries don't dump into whatever thread the user is in.
  // The thread is just an ordinary user-visible thread now (no longer
  // hidden) — the user can rename/archive it.
  const captureThreadIdRef = useRef<string | null>(null)
  useEffect(() => {
    captureThreadIdRef.current = null
  }, [project?.id])

  const findOrCreateCaptureThread = useCallback(async (): Promise<string | null> => {
    if (!project) return null
    if (captureThreadIdRef.current) return captureThreadIdRef.current
    try {
      const ths = await api.listThreads(project.id)
      const existing = ths.find((t) => t.title === 'Mobile Capture')
      if (existing) {
        captureThreadIdRef.current = existing.id
        return existing.id
      }
      const created = await createThreadWithPreference(project.id, 'Mobile Capture')
      captureThreadIdRef.current = created.id
      // Surface it in the drawer too.
      setThreads((prev) => [created, ...prev.filter((t) => t.id !== created.id)])
      return created.id
    } catch (e) {
      setError(`thread setup failed: ${(e as Error).message}`)
      return null
    }
  }, [project])

  const wireForCapture = useCallback(
    (text: string) =>
      `Capture this as a fact card (use write_card, kind=fact when permitted, otherwise observation):\n\n${text}`,
    [],
  )

  // PR-28: drain the queue on mount AND whenever we transition to online.
  // Each entry is sent through the same streamMessage path as a fresh
  // capture — failures stay in the queue with backoff metadata.
  const flushQueueRef = useRef<() => Promise<void>>(async () => {})
  flushQueueRef.current = async () => {
    if (!online) return
    if (!project) return
    const tid = await findOrCreateCaptureThread()
    if (!tid) return
    const result = await captureQueue.flush(
      async (entry) => {
        await api.streamMessage(tid, wireForCapture(entry.text), () => {
          /* ignore */
        })
      },
      { ignoreBackoff: true, project_id: project.id },
    )
    await refreshQueueCount()
    if (result.sent > 0) {
      setToast(
        `Synced ${result.sent} queued capture${result.sent === 1 ? '' : 's'}`,
      )
      // Refresh threads so the drawer reflects any messages added to the
      // capture thread (its updated_at moves).
      await refreshThreads(project.id)
    }
    if (result.giving_up.length > 0) {
      setError(
        `${result.giving_up.length} capture${result.giving_up.length === 1 ? '' : 's'} failed after ${captureQueue._MAX_ATTEMPTS} retries`,
      )
    }
  }

  useEffect(() => {
    refreshQueueCount().catch(() => {})
    if (!online || !project) return
    void flushQueueRef.current()
  }, [online, project?.id, refreshQueueCount])

  // ── Gap actions ─────────────────────────────────────────────────
  const onApproveGap = useCallback(
    async (cardId: string) => {
      if (!project) return
      try {
        const promoted = await api.promoteCard(project.id, cardId, {})
        setCards((prev) =>
          prev.map((c) => (c.card_id === promoted.card_id ? promoted : c)),
        )
        setToast(`Approved ${promoted.card_id} → ${promoted.kind}`)
      } catch (e) {
        setError(`approve failed: ${(e as Error).message}`)
      }
    },
    [project],
  )

  const onRejectGap = useCallback(
    async (cardId: string) => {
      if (!project) return
      try {
        const out = await api.rejectGap(project.id, cardId)
        setCards((prev) => prev.filter((c) => c.card_id !== out.card_id))
        if (out.blocks_marked.length > 0) {
          api.listCards(project.id).then(setCards).catch(() => {})
        }
        setToast(`Rejected ${out.card_id}`)
      } catch (e) {
        setError(`reject failed: ${(e as Error).message}`)
      }
    },
    [project],
  )

  // Soft-archive a kind=question card. The card stays in the DB so
  // wikilinks resolve, but it falls out of OPEN QUESTIONS. Mirrors
  // App.tsx onArchiveQuestion (9a4535c).
  const onArchiveQuestion = useCallback(
    async (cardId: string) => {
      if (!project) return
      try {
        const updated = await api.setCardArchived(project.id, cardId, true)
        setCards((prev) =>
          prev.map((c) => (c.card_id === updated.card_id ? updated : c)),
        )
        setToast('Archived')
      } catch (e) {
        setError(`archive failed: ${(e as Error).message}`)
      }
    },
    [project],
  )

  // Bulk-approve a batch of agent proposals — promotes each in parallel,
  // then surfaces an Undo toast for 5s. Mirrors App.tsx onApproveProposalBatch
  // (9a4535c). Undo path deletes the promoted cards (since /reject only
  // accepts kind=gap and these cards are no longer gaps post-promote).
  const onApproveBatch = useCallback(
    async (cardIds: string[]) => {
      if (!project || cardIds.length === 0) {
        return { ok: [] as string[], failed: [] as string[] }
      }
      const pid = project.id
      const results = await Promise.allSettled(
        cardIds.map((cid) => api.promoteCard(pid, cid, {})),
      )
      const ok: string[] = []
      const failed: string[] = []
      const promotedCards: Card[] = []
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          ok.push(cardIds[i])
          promotedCards.push(r.value)
        } else {
          failed.push(cardIds[i])
        }
      })
      if (promotedCards.length > 0) {
        setCards((prev) => {
          const byId = new Map(prev.map((c) => [c.card_id, c]))
          for (const fresh of promotedCards) byId.set(fresh.card_id, fresh)
          return Array.from(byId.values())
        })
      }
      if (failed.length > 0) {
        setError(`bulk approve: ${failed.length} of ${cardIds.length} failed`)
      }
      if (promotedCards.length > 0) {
        // Destination breakdown for the toast: how many landed in OPEN
        // (kind=question) vs cards (everything else). Mirrors desktop.
        const toOpen = promotedCards.filter((c) => c.kind === 'question').length
        const toCards = promotedCards.length - toOpen
        const parts: string[] = []
        if (toCards > 0) parts.push(`${toCards}→cards`)
        if (toOpen > 0) parts.push(`${toOpen}→OPEN`)
        const msg = `✓ ${promotedCards.length} accepted · ${parts.join(' · ')}`
        const ids = promotedCards.map((c) => c.card_id)
        showUndoToast(msg, {
          label: 'Undo',
          onClick: async () => {
            const undoResults = await Promise.allSettled(
              ids.map((cid) => api.deleteCard(pid, cid)),
            )
            setCards((prev) => prev.filter((c) => !ids.includes(c.card_id)))
            const undoneFails = undoResults.filter(
              (r) => r.status === 'rejected',
            ).length
            if (undoneFails > 0) {
              setError(`undo: ${undoneFails} of ${ids.length} delete failed`)
            } else {
              setToast(`Undid ${ids.length} card${ids.length === 1 ? '' : 's'}`)
            }
          },
        })
      }
      return { ok, failed }
    },
    [project, showUndoToast],
  )

  // Bulk-dismiss — sequential calls into rejectGap. No undo (rejection
  // also writes dismissed_gaps marks the agent honours next audit, so
  // "undo" would have to clear those too — more surgery than the
  // savings warrant; matches desktop's choice).
  const onRejectBatch = useCallback(
    async (cardIds: string[]) => {
      if (!project || cardIds.length === 0) {
        return { ok: [] as string[], failed: [] as string[] }
      }
      const ok: string[] = []
      const failed: string[] = []
      for (const cid of cardIds) {
        try {
          await api.rejectGap(project.id, cid)
          ok.push(cid)
        } catch {
          failed.push(cid)
        }
      }
      if (ok.length > 0) {
        setCards((prev) => prev.filter((c) => !ok.includes(c.card_id)))
      }
      if (failed.length > 0) {
        setError(`bulk dismiss: ${failed.length} of ${cardIds.length} failed`)
      } else if (ok.length > 0) {
        setToast(`Dismissed ${ok.length}`)
      }
      return { ok, failed }
    },
    [project],
  )

  // PR-34: Discuss flow — find an existing thread already pinned to this
  // gap; if none, create a new "Discuss · {seed}" thread, pin it to the
  // gap, switch to it, and fire a synthetic "opener" turn that frames
  // the discussion. Mirrors desktop App.tsx:1485.
  // Discuss/Answer a card. Accepts kind=gap (Discuss button on a proposal
  // row) AND kind=question (Answer button in OPEN QUESTIONS — desktop
  // routes both through the same handler, mirror that here):
  //   - gap      : auto-fires the synthetic opener turn so the agent
  //                immediately restates the proposal context.
  //   - question : opens the thread but lets the user write the first
  //                turn. The question title alone usually isn't enough
  //                framing for the agent to start drafting.
  const onDiscussGap = useCallback(
    async (cardId: string) => {
      if (!project) return
      // Dedup: if any thread already pins this card, just switch to it.
      const existingTid = Object.entries(pinnedGapByThread).find(
        ([, gid]) => gid === cardId,
      )?.[0]
      if (existingTid && threads.some((t) => t.id === existingTid)) {
        setActiveThreadId(existingTid)
        api.setLastThread(project.id, existingTid).catch(() => {})
        setTab('chat')
        setDrawerOpen(false)
        return
      }
      const card = cards.find(
        (c) => c.card_id === cardId && (c.kind === 'gap' || c.kind === 'question'),
      )
      if (!card) {
        setError('card not found')
        return
      }
      const seed =
        card.title?.trim() ||
        card.body.split('\n').find((l) => l.trim().length > 0)?.trim() ||
        cardId
      const isQuestion = card.kind === 'question'
      const title = `${isQuestion ? 'Answer' : 'Discuss'} · ${seed}`.slice(0, 70)
      try {
        const t = await createThreadWithPreference(project.id, title)
        setThreads((prev) => [t, ...prev])
        setMessagesByThread((prev) => ({ ...prev, [t.id]: [] }))
        setPinnedGapByThread((prev) => ({ ...prev, [t.id]: cardId }))
        setActiveThreadId(t.id)
        api.setLastThread(project.id, t.id).catch(() => {})
        setTab('chat')
        setDrawerOpen(false)
        // Skip the synthetic opener for questions — only the gap branch
        // wants the agent to take the first turn.
        if (isQuestion) return
        // Fire opener: agent restates the gap and proposes sub-questions.
        // Skip the user bubble and auto-rename — opener is synthetic.
        const tid = t.id
        const wireText = buildDiscussingGapWire(card, cards, '', { opener: true })
        setBusyByThread((prev) => ({ ...prev, [tid]: true }))
        setThreadProjectMap((prev) =>
          prev[tid] === project.id ? prev : { ...prev, [tid]: project.id },
        )
        try {
          await api.streamMessage(tid, wireText, (evt: TurnEvent) => {
            handleStreamEvent(tid, evt)
          })
        } finally {
          commitStreaming(tid)
          setBusyByThread((prev) => {
            if (!(tid in prev)) return prev
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [tid]: _gone, ...rest } = prev
            return rest
          })
          // Mirror desktop: optimistic last_message_at bump so the
          // unread dot appears for off-screen viewers.
          const finishedAt = new Date().toISOString()
          setThreads((arr) =>
            arr.map((t2) =>
              t2.id === tid
                ? { ...t2, last_message_at: finishedAt, updated_at: finishedAt }
                : t2,
            ),
          )
        }
      } catch (e) {
        setError(`create discuss thread failed: ${(e as Error).message}`)
      }
    },
    [project, pinnedGapByThread, threads, cards, handleStreamEvent, commitStreaming],
  )

  // PR-35: distill — fires api.distillThread and pumps events into the
  // modal's state. Mirrors desktop App.tsx onDistill (line 1150). The
  // `mode` arg lets onAuditGaps reuse this with mode='audit'.
  const onDistill = useCallback(
    async (mode: DistillMode = 'rescue') => {
      if (!activeThreadId) {
        setError('open or create a thread first')
        return
      }
      if (distilling) return
      // Prevent firing distill while the active thread is mid-turn —
      // distill reads the session and concurrent writes are confusing.
      if (activeThreadId && busyByThread[activeThreadId]) {
        setError('wait for the current turn to finish before distilling')
        return
      }
      setDistilling(true)
      setDistillOpen(true)
      setDistillItems(0)
      setDistillCards(null)
      setDistillGaps(null)
      setDistillError(null)
      setDistillMode(mode)
      try {
        await api.distillThread(
          activeThreadId,
          (evt) => {
            if (evt.type === 'tick') setDistillItems(evt.items)
            else if (evt.type === 'done') {
              setDistillCards(evt.cards)
              setDistillGaps(evt.gaps)
            } else if (evt.type === 'error') {
              setDistillError(evt.message)
            }
          },
          mode,
        )
      } catch (e) {
        setDistillError((e as Error).message)
      } finally {
        setDistilling(false)
      }
    },
    [activeThreadId, distilling, busyByThread],
  )

  // Audit gaps button on the Gaps tab → distill in audit mode.
  const onAuditGaps = useCallback(() => {
    void onDistill('audit')
    setTab('chat') // keep the user near where the audit's results land
  }, [onDistill])

  // User accepted a subset from the distill modal. We hand the full
  // structured request to the agent so it can dedupe / spot conflicts /
  // route writes through the same SSE path live cards use. Mirrors the
  // wire format desktop App.tsx onDistillAccept (line 1199).
  const onDistillAccept = useCallback(
    (chosen: { cards: CardProposal[]; gaps: GapProposal[] }) => {
      setDistillOpen(false)
      if (chosen.cards.length === 0 && chosen.gaps.length === 0) return

      const blocks: string[] = []
      if (chosen.cards.length > 0) {
        const lines = chosen.cards.map(
          (p, i) =>
            `${i + 1}. (${p.kind}) ${p.title}\n   ${p.body.replace(/\n/g, ' ')}`,
        )
        blocks.push(
          `## Cards to write (use write_card)\n` +
            `These are crystallised — I confirmed them. Write each as the kind shown.\n\n` +
            lines.join('\n'),
        )
      }
      if (chosen.gaps.length > 0) {
        const lines = chosen.gaps.map((g, i) => {
          const blocksStr = g.blocks && g.blocks.length > 0
            ? ` blocks=[${g.blocks.join(', ')}]`
            : ''
          const priorityStr = g.priority ? ` priority=${g.priority}` : ''
          return (
            `${i + 1}. (proposed_kind=${g.proposed_kind})${blocksStr}${priorityStr}\n` +
            `   title: ${g.title}\n` +
            `   body: ${g.body.replace(/\n/g, ' ')}\n` +
            `   ambiguity_reason: ${g.ambiguity_reason}`
          )
        })
        blocks.push(
          `## Gaps to propose (use propose_card)\n` +
            `These are open items — I want them in the Gap Ledger so I can Approve / Discuss / Reject.\n\n` +
            lines.join('\n'),
        )
      }
      const text = `[from-distill ${distillMode}]\n\n${blocks.join('\n\n')}`
      void onSendChatMessage(text)
    },
    [distillMode, onSendChatMessage],
  )

  // PR-32: cross-tab jump — opens the Cards tab with the target card peeked.
  // Used by wikilinks in chat, by tool-call chips that resolve to a card,
  // and by the pinned-gap banner's title.
  const onJumpToCard = useCallback((cardId: string) => {
    setPeekedCardId(cardId)
    setTab('cards')
    setDrawerOpen(false)
  }, [])

  // PR-33: save patched card from MobileCardPeek. Optimistic update so
  // the form snaps closed without waiting; the SSE refresh (api.events
  // listener above) reconciles to authoritative server state.
  const onSaveCard = useCallback(
    async (cardId: string, patch: CardPatch) => {
      if (!project) throw new Error('no active project')
      const updated = await api.patchCard(project.id, cardId, patch)
      setCards((prev) =>
        prev.map((c) => (c.card_id === updated.card_id ? updated : c)),
      )
      setToast(`Saved ${updated.card_id}`)
    },
    [project],
  )

  // knownIds for the active project — passed to MobileCardPeek so
  // wikilinks resolve. Stable identity across re-renders unless the card
  // list itself changes.
  const knownIds = useMemo(
    () => new Set(cards.map((c) => c.card_id)),
    [cards],
  )

  const peekedCard = useMemo(() => {
    if (!peekedCardId) return null
    return cards.find((c) => c.card_id === peekedCardId) ?? null
  }, [peekedCardId, cards])

  useEffect(() => {
    if (peekedCardId && !peekedCard) setPeekedCardId(null)
  }, [peekedCardId, peekedCard])

  const activeThread = useMemo(
    () => (activeThreadId ? threads.find((t) => t.id === activeThreadId) ?? null : null),
    [activeThreadId, threads],
  )

  // PR-58 mobile: change the active thread's tier. Mirrors desktop
  // App.tsx onChangeModelPreference: optimistic local update → server
  // PATCH → setPreferredTier so future new threads inherit the same
  // pick. Failure rolls the local row back. Effort/fast_mode aren't
  // exposed on mobile yet; the server's resolve_chat_preference will
  // pick `default_effort` from the tier registry on its own.
  const onChangeTier = useCallback(
    async (tierId: string | null) => {
      // Always remember the choice locally — even if there's no
      // active thread (the user opened the picker before sending),
      // the next createThreadWithPreference will apply it.
      setPreferredTier(tierId)
      if (!activeThreadId) return
      const tid = activeThreadId
      const before = threads.find((t) => t.id === tid)
      if (!before) return
      const optimistic = { ...before, model: tierId }
      setThreads((arr) => arr.map((t) => (t.id === tid ? optimistic : t)))
      try {
        const updated = await api.setThreadModelPreference(tid, {
          model: tierId,
        })
        setThreads((arr) => arr.map((t) => (t.id === tid ? updated : t)))
      } catch (e) {
        setThreads((arr) => arr.map((t) => (t.id === tid ? before : t)))
        setError(`set model failed: ${(e as Error).message}`)
      }
    },
    [activeThreadId, threads],
  )
  const activeMessages = useMemo(() => {
    if (!activeThreadId) return EMPTY_MESSAGES
    const base = messagesByThread[activeThreadId] ?? EMPTY_MESSAGES
    const stream = streamingByThread[activeThreadId] ?? EMPTY_MESSAGES
    if (stream.length === 0) return base
    if (base.length === 0) return stream
    return [...base, ...stream]
  }, [activeThreadId, messagesByThread, streamingByThread])
  const activeBusy = activeThreadId ? !!busyByThread[activeThreadId] : false

  // Project ids hosting at least one busy thread. Same shape as desktop;
  // drives the breathing dot on off-screen project rows in the side drawer.
  const busyProjects = useMemo(() => {
    const set = new Set<string>()
    for (const tid of Object.keys(busyByThread)) {
      if (!busyByThread[tid]) continue
      const pid = threadProjectMap[tid]
      if (pid) set.add(pid)
    }
    return set
  }, [busyByThread, threadProjectMap])

  // Mirror of App.tsx unread derivation. Active thread is always 0; a
  // thread without a cursor counts as unread only if it has activity
  // past created_at (so a freshly-created thread you're typing in
  // doesn't badge you).
  const unreadByThread = useMemo(() => {
    const map: Record<string, number> = {}
    for (const t of threads) {
      if (t.id === activeThreadId) continue
      const lm = t.last_message_at ?? t.updated_at
      const lr = t.last_read_at ?? null
      const isUnread = lr === null ? lm > t.created_at : lm > lr
      if (isUnread) map[t.id] = 1
    }
    return map
  }, [threads, activeThreadId])

  // Keep active project's unread_thread_count in sync with local threads
  // (mirror of desktop App.tsx — same stale-count-on-project-switch bug).
  useEffect(() => {
    if (!project) return
    const localUnread = threads.reduce((n, t) => {
      const lm = t.last_message_at ?? t.updated_at
      const lr = t.last_read_at ?? null
      const isUnread = lr === null ? lm > t.created_at : lm > lr
      return n + (isUnread ? 1 : 0)
    }, 0)
    setProjects((arr) =>
      arr.map((p) =>
        p.id === project.id && p.unread_thread_count !== localUnread
          ? { ...p, unread_thread_count: localUnread }
          : p,
      ),
    )
  }, [threads, project])

  const unreadProjects = useMemo(() => {
    const set = new Set<string>()
    for (const p of projects) {
      if (p.id === project?.id) continue
      if ((p.unread_thread_count ?? 0) > 0) set.add(p.id)
    }
    if (project) {
      const hasUnread = threads.some((t) => {
        if (t.id === activeThreadId) return false
        const lm = t.last_message_at ?? t.updated_at
        const lr = t.last_read_at ?? null
        return lr === null ? lm > t.created_at : lm > lr
      })
      if (hasUnread) set.add(project.id)
    }
    return set
  }, [projects, project, threads, activeThreadId])

  // PR-32: pinned gap for the active thread. Resolved against the live card
  // list so we drop the pin if the gap was deleted/promoted out from under us.
  const pinnedGapForActiveThread = useMemo(() => {
    if (!activeThreadId) return null
    const cardId = pinnedGapByThread[activeThreadId]
    if (!cardId) return null
    const c = cards.find((c) => c.card_id === cardId) ?? null
    if (!c || c.kind !== 'gap') return null
    return c
  }, [activeThreadId, pinnedGapByThread, cards])

  // Auto-clear stale pins (gap card disappeared or got promoted). Mirrors
  // desktop App.tsx validation effect.
  useEffect(() => {
    if (!activeThreadId) return
    const pinnedId = pinnedGapByThread[activeThreadId]
    if (!pinnedId) return
    const c = cards.find((c) => c.card_id === pinnedId)
    if (!c || c.kind !== 'gap') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPinnedGapByThread((prev) => {
        if (!(activeThreadId in prev)) return prev
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [activeThreadId]: _gone, ...rest } = prev
        return rest
      })
    }
  }, [activeThreadId, pinnedGapByThread, cards])

  const onResolveGap = useCallback(() => {
    if (!activeThreadId) return
    setPinnedGapByThread((prev) => {
      if (!(activeThreadId in prev)) return prev
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [activeThreadId]: _gone, ...rest } = prev
      return rest
    })
  }, [activeThreadId])

  const onApprovePinnedGap = useCallback(
    async (cardId: string) => {
      await onApproveGap(cardId)
      // Approve flips the gap to its proposed_kind; the validation effect
      // above will drop the pin once `cards` updates, but we eagerly clear
      // here too so the banner disappears immediately.
      onResolveGap()
    },
    [onApproveGap, onResolveGap],
  )

  const onRejectPinnedGap = useCallback(
    async (cardId: string) => {
      await onRejectGap(cardId)
      onResolveGap()
    },
    [onRejectGap, onResolveGap],
  )

  const topbarTitle = useMemo(() => {
    if (tab === 'chat') {
      if (activeThread) return activeThread.title?.trim() || 'untitled'
      return 'New chat'
    }
    if (tab === 'cards') return 'Cards'
    return 'Gaps'
  }, [tab, activeThread])

  // PR-54: manual + auto refresh. iOS Capacitor backgrounds the WebView
  // when the user switches apps; the SSE EventSource that drives live
  // card/thread updates dies and surfaces stale data on resume. Refresh
  // re-pulls cards + threads + drops the active thread's message cache so
  // the load effect re-fetches messages.
  const refreshAll = useCallback(async () => {
    if (!project) return
    setToast('Refreshing…')
    try {
      await Promise.all([
        refreshCards(project.id),
        refreshThreads(project.id),
      ])
      if (activeThreadId) {
        setMessagesByThread((prev) => {
          if (!(activeThreadId in prev)) return prev
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [activeThreadId]: _gone, ...rest } = prev
          return rest
        })
        dropStreaming(activeThreadId)
      }
      setToast('Up to date')
    } catch (e) {
      setError(`refresh failed: ${(e as Error).message}`)
    }
  }, [project, activeThreadId, refreshCards, refreshThreads, dropStreaming])

  // PR-54: when the iOS Capacitor app comes back to the foreground, the
  // SSE connection has typically died. Auto-refresh so the user doesn't
  // see stale state. No-op in browser.
  useEffect(() => {
    return listenToAppResume(() => {
      void (async () => {
        await refreshAll()
        // If we backgrounded mid-turn, refreshAll's listMessages refetch
        // picks up everything persisted UP TO the last completed turn —
        // the still-in-flight turn's events live in the server's hub
        // buffer and won't land in session_blob until it finishes.
        // Ask the server whether the active thread still has a turn
        // running; if so, flip busy so the header shows "responding"
        // and let the project-SSE `thread_turn_ended` handler land the
        // canonical messages once it actually completes.
        if (!activeThreadId) return
        try {
          const active = await api.getActiveTurn(activeThreadId)
          if (active) {
            setBusyByThread((prev) =>
              prev[active.thread_id]
                ? prev
                : { ...prev, [active.thread_id]: true },
            )
          }
        } catch {
          /* non-fatal — fresh thread_turn_started will set busy if any */
        }
      })()
    })
  }, [refreshAll, activeThreadId])

  // PR-35: per-tab actions for the topbar `⋯` menu. Chat surfaces Distill
  // (also Rename / Archive shortcuts that the drawer's long-press already
  // exposes — kept here for one-tap access on a power-user flow). Cards
  // surfaces Documents. Gaps has Audit as a primary button so no `⋯`.
  // PR-54: every tab gets a "Refresh" entry — critical on iOS where the
  // SSE connection dies on backgrounding.
  const topbarActions = useMemo<TopBarAction[] | undefined>(() => {
    const refreshEntry: TopBarAction = {
      id: 'refresh',
      label: 'Refresh',
      hint: 'pull latest',
      disabled: !project,
      onSelect: () => void refreshAll(),
    }
    if (tab === 'chat') {
      const out: TopBarAction[] = [refreshEntry]
      out.push({
        id: 'distill',
        label: 'Distill',
        hint: 'rescue mode',
        disabled: !activeThreadId || activeBusy || distilling,
        onSelect: () => void onDistill('rescue'),
      })
      if (activeThread) {
        out.push({
          id: 'rename',
          label: 'Rename thread',
          onSelect: () => {
            const next = window.prompt('Rename thread', activeThread.title ?? '')
            if (next === null) return
            const trimmed = next.trim()
            if (trimmed && trimmed !== (activeThread.title ?? '')) {
              void onRenameThread(activeThread.id, trimmed)
            }
          },
        })
        out.push({
          id: 'archive',
          label: 'Archive thread',
          destructive: true,
          onSelect: () => void onArchiveThread(activeThread.id),
        })
      }
      return out
    }
    if (tab === 'cards') {
      return [
        refreshEntry,
        {
          id: 'documents',
          label: 'Documents',
          hint: 'view-only',
          disabled: !project,
          onSelect: () => setDocumentsOpen(true),
        },
      ]
    }
    if (tab === 'gaps') {
      return [refreshEntry]
    }
    return undefined
  }, [
    tab,
    activeThread,
    activeThreadId,
    activeBusy,
    distilling,
    onDistill,
    onRenameThread,
    onArchiveThread,
    project,
    refreshAll,
  ])

  if (bootStage === 'loading') {
    return (
      <div className="mobile-shell">
        <div className="mobile-splash mono">loading…</div>
      </div>
    )
  }
  if (bootStage === 'empty') {
    return (
      <div className="mobile-shell">
        <div className="mobile-splash mono">no project — open desktop to set up</div>
      </div>
    )
  }

  return (
    <div className="mobile-shell">
      {showWechatBanner && (
        <div
          className="mobile-wechat-banner"
          onClick={() => setShowWechatBanner(false)}
          role="button"
          aria-label="Dismiss WeChat tip"
        >
          Tip: open in browser for the best experience · tap to dismiss
        </div>
      )}
      <MobileTopBar
        title={topbarTitle}
        subtitle={project?.name ?? null}
        onOpenDrawer={() => setDrawerOpen(true)}
        online={online}
        queueCount={queueCount}
        actions={topbarActions}
      />
      <main className="mobile-body">
        {tab === 'chat' && (
          <MobileChat
            threadTitle={activeThread?.title ?? null}
            hasActiveThread={activeThread !== null}
            messages={activeMessages}
            busy={activeBusy}
            disabled={!project}
            onSend={onSendChatMessage}
            cards={cards}
            onJumpToCard={onJumpToCard}
            pinnedGap={pinnedGapForActiveThread}
            onResolveGap={onResolveGap}
            onApprovePinnedGap={(id) => void onApprovePinnedGap(id)}
            onRejectPinnedGap={(id) => void onRejectPinnedGap(id)}
            modelChoices={modelChoices}
            threadModel={activeThread?.model ?? null}
            onChangeTier={onChangeTier}
            usage={activeThreadId ? usageByThread[activeThreadId] ?? null : null}
          />
        )}
        {tab === 'cards' && (
          <MobileCards
            projectId={project?.id ?? null}
            cards={cards}
            cardsRevision={cardsRevision}
            onPeek={setPeekedCardId}
            onRefresh={() => {
              if (project) return refreshCards(project.id)
              return Promise.resolve()
            }}
            onFrameGapsChange={setFrameGaps}
          />
        )}
        {tab === 'gaps' && (
          <MobileGaps
            cards={cards}
            frameGaps={frameGaps}
            onApprove={onApproveGap}
            onReject={onRejectGap}
            onDiscuss={(id) => void onDiscussGap(id)}
            onAnswerQuestion={(id) => void onDiscussGap(id)}
            onArchiveQuestion={onArchiveQuestion}
            onApproveBatch={onApproveBatch}
            onRejectBatch={onRejectBatch}
            onPeek={setPeekedCardId}
            onAudit={onAuditGaps}
          />
        )}
      </main>
      <MobileBottomNav active={tab} onChange={setTab} />

      <MobileSideDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        projects={projects}
        activeProject={project}
        onSelectProject={(p) => void selectProject(p)}
        threads={threads}
        activeThreadId={activeThreadId}
        onSelectThread={onSelectThread}
        onNewThread={() => void onNewThread()}
        onRenameThread={onRenameThread}
        onArchiveThread={(id) => void onArchiveThread(id)}
        me={me}
        onLogout={onLogout}
        busyByThread={busyByThread}
        busyProjects={busyProjects}
        unreadByThread={unreadByThread}
        unreadProjects={unreadProjects}
      />

      {peekedCard && (
        <MobileCardPeek
          card={peekedCard}
          knownIds={knownIds}
          onClose={() => setPeekedCardId(null)}
          onSave={(patch) => onSaveCard(peekedCard.card_id, patch)}
          onJumpToCard={onJumpToCard}
        />
      )}

      <MobileDistillModal
        open={distillOpen}
        itemsSoFar={distillItems}
        cards={distillCards}
        gaps={distillGaps}
        errorMsg={distillError}
        mode={distillMode}
        busy={distilling}
        onChangeMode={(next) => void onDistill(next)}
        onClose={() => setDistillOpen(false)}
        onAccept={onDistillAccept}
      />

      <MobileDocumentsModal
        open={documentsOpen}
        projectId={project?.id ?? null}
        onClose={() => setDocumentsOpen(false)}
      />

      {(error || toast) && (
        <div
          className={`mobile-toast${error ? ' mobile-toast--error' : ''}${
            toastAction ? ' mobile-toast--with-action' : ''
          }`}
          onClick={() => {
            // Tap on the toast row dismisses; the action button calls
            // stopPropagation so it can fire its own onClick first.
            setError(null)
            setToast(null)
            setToastAction(null)
          }}
        >
          <span className="mobile-toast__text">{error || toast}</span>
          {!error && toastAction && (
            <button
              type="button"
              className="mobile-toast__action mono"
              onClick={(e) => {
                e.stopPropagation()
                const action = toastAction
                setToast(null)
                setToastAction(null)
                void action.onClick()
              }}
            >
              {toastAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// First ~40 chars of the user's first turn make a decent thread title.
// Kept inline (vs imported from a shared util) so the mobile shell has no
// new cross-file dependency just for this.
function deriveThreadTitle(text: string): string | null {
  const trimmed = text.trim().replace(/\s+/g, ' ')
  if (!trimmed) return null
  return trimmed.length > 40 ? trimmed.slice(0, 40) + '…' : trimmed
}
